// Imports
import { faCreditCard } from '@fortawesome/pro-regular-svg-icons';
import { faUniversity } from '@fortawesome/pro-solid-svg-icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import LoadingRow from '../../../../../../shareables/foundation/front-end/components/tables/loading-row';
import modalHelpers from '../../../../../../shareables/front-end/modal/modal-helpers';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import APIForm, { APIFormOptions } from '../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import { CheckCardVerificationParameters } from '../../../../../../shareables/types/api/main/website/member/payment-method/card/verification/check';
import { GetSetupIntentResponse } from '../../../../../../shareables/types/api/main/website/member/payment-method/setup-intent/get';
import { GetPaymentMethodsResponse } from '../../../../../../shareables/types/api/main/website/member/payment-methods/get';
import isScreenshot from '../../../../../../shareables/utils/is-screenshot';
import Panel from '../../page-types/shared-components/panel';
import Table from '../../page-types/shared-components/table';
import EmptyStateRow from '../../page-types/shared-components/table/empty-state-row';
import UserControlButton from '../../page-types/shared-components/user-control-button';
import MicrodepositInfoModal from './microdeposit-info-modal';
import StoredPaymentMethodRows from './row';
import StripeElements from './stripe-elements';
import { CardSetupIntentHandlerRef } from './card-setup-intent-handler';
import useStripeInstance from '../hooks/use-stripe-instance';
import VerifyBankAccountModal from './verify-bank-account-modal';
import commonActions from '../../../../../../shareables/foundation/front-end/redux/actions';
import BankAccountSetupIntentHandler, { BankAccountSetupIntentHandlerRef } from './bank-account-setup-intent-handler';
import removePersistedFormEntry from '../../../../../../utils/remove-persisted-form-entry';
import helperFunctions from '../../../../../../shareables/foundation/front-end/utils/helper-functions';
import { GeneratedThemeProps } from '../../../../../templates/template-style';
import PageTitle from '../../shared-components/page-title';
import Notice from '../../../../notice';


// Styled components
const Note = styled.p`
	line-height: 1.3125rem;
	margin-bottom: 1.3rem;
`;

const ButtonGroup = styled.div`
	display: flex;
	gap: 0.3rem;
	flex-wrap: wrap;
	
	& > *:not(:last-child) {
		margin-bottom: 0;
	}
	
	.svg-inline--fa {
		padding-right: 0.3125rem;
	}
`;

const FormWrapper = styled.div`
	form {
		button {
			margin-top: calc(1.25em - 0.25rem);
		}
	}
`;

const PanelContainer = styled.div<{ open: boolean }>`
	display: grid;
	grid-template-rows: ${({ open }) => (open ? '1fr' : '0fr')};
	transition:
		grid-template-rows 400ms ease,
		opacity 400ms ease;
	opacity: ${({ open }) => (open ? 1 : 0)};
`;

const PanelContainerInner = styled.div`
	overflow: hidden;
	min-height: 0;
`;

const ContentSection = styled.div<{ open: boolean }>`
	display: grid;
	grid-template-rows: ${({ open }) => (open ? '1fr' : '0fr')};
`;

const ContentSectionInner = styled.div`
	overflow: hidden;
	min-height: 0;
	margin-left: -0.25rem;
	padding-left: 0.25rem;
	margin-right: -0.25rem;
	padding-right: 0.25rem;
`;

const ContentSectionContent = styled.div`
	padding-bottom: 0.25rem;
`;

const TextLink = styled.span<{ theme: GeneratedThemeProps }>`
	text-decoration: underline;
	cursor: pointer;
	
	&:hover {
		text-decoration: none;
	}
`;

export type UnverifiedBankAccount = Extract<
	GetPaymentMethodsResponse['paymentMethods'][0],
	{ type: 'BANK_ACCOUNT'; status: 'PENDING' }
>;


// Define the accepted props
interface Props {
	storybookPaymentMethods?: GetPaymentMethodsResponse;
	storybookSelectedPaymentMethodType?: string;
}


// Function component
const Methods: React.FC<Props> = ({ storybookPaymentMethods, storybookSelectedPaymentMethodType }) => {
	// Use state
	const [paymentMethods, setPaymentMethods] = useState<GetPaymentMethodsResponse | null>(
		storybookPaymentMethods ?? null
	);
	
	const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<string>(
		storybookSelectedPaymentMethodType ?? ''
	);
	
	const [renderedPaymentMethodType, setRenderedPaymentMethodType] = useState<string>(
		storybookSelectedPaymentMethodType ?? ''
	);
	
	const [formKey, setFormKey] = useState(0);
	const [verificationTarget, setVerificationTarget] = useState<UnverifiedBankAccount | null>(null);
	const [learnMoreTarget, setLearnMoreTarget] = useState<UnverifiedBankAccount | null>(null);
	const [successMicrodepositType, setSuccessMicrodepositType] =
		useState<UnverifiedBankAccount['verification']['microdeposit']>();
	
	
	// Use ref
	const cardSetupIntentHandlerRef = useRef<CardSetupIntentHandlerRef>(null);
	const bankAccountSetupIntentHandlerRef = useRef<BankAccountSetupIntentHandlerRef>(null);
	
	
	// Use Stripe hook
	const stripePromise = useStripeInstance(paymentMethods?.stripeAccountID ?? '');
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Function that loads payment methods
	const memoizedLoadPaymentMethods = useCallback(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetPaymentMethodsResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/member/payment-methods`,
				completion: (json) => {
					setPaymentMethods(json.response);
				},
			})
		);
	}, [dispatch]);
	
	
	// Load payment methods on mount
	useEffect(() => {
		memoizedLoadPaymentMethods();
	}, [memoizedLoadPaymentMethods]);
	
	
	// Handle verify bank account modal
	useEffect(() => {
		if (verificationTarget) {
			modalHelpers.show('verify-bank-account-modal', {
				onClose: () => setVerificationTarget(null),
			});
		}
	}, [verificationTarget]);
	
	
	// Handle learn more modal
	useEffect(() => {
		if (learnMoreTarget) {
			modalHelpers.show('microdeposit-info-modal', {
				onClose: () => setLearnMoreTarget(null),
			});
		}
	}, [learnMoreTarget]);
	
	
	// Handle panel open/close
	const handleOpen = (type: string) => {
		setSelectedPaymentMethodType(type);
		setRenderedPaymentMethodType(type);
	};
	
	const handleClose = () => {
		removePersistedFormEntry(`GET /website/${window.location.hostname}/member/payment-method/setup-intent`);
		setSelectedPaymentMethodType('');
	};
	
	const handleTransitionEnd = (e: React.TransitionEvent) => {
		if (e.target === e.currentTarget && e.propertyName === 'grid-template-rows' && !selectedPaymentMethodType) {
			setRenderedPaymentMethodType('');
			setFormKey((previousKey) => previousKey + 1);
		}
	};
	
	
	// Build form options
	const formOptions: APIFormOptions<undefined, undefined, GetSetupIntentResponse> = {
		action: 'GET',
		uri: `/website/${window.location.hostname}/member/payment-method/setup-intent`,
		submitButton: {
			text: 'Add',
		},
		cancelButton: {
			text: 'Cancel',
		},
	};
	
	
	// Reset the form and reload payment methods after a successful add
	const resetAfterPaymentMethodAdded = () => {
		removePersistedFormEntry(`GET /website/${window.location.hostname}/member/payment-method/setup-intent`);
		setFormKey((previousKey) => previousKey + 1);
		setSelectedPaymentMethodType('');
		memoizedLoadPaymentMethods();
	};
	
	
	// Handle form completion
	const handleFormCompletion = async (json: SuccessfulAPIResponse<GetSetupIntentResponse>) => {
		if (selectedPaymentMethodType === 'us_bank_account') {
			// Submit and retrieve setup intent details
			const setupIntent = await bankAccountSetupIntentHandlerRef.current?.handleSubmit(json.response);
			
			
			// Return early
			if (!setupIntent) {
				return;
			}
			
			
			// Initialize
			const microdepositType =
				setupIntent.next_action?.verify_with_microdeposits?.microdeposit_type === 'descriptor_code'
					? 'code'
					: 'amounts';
			
			
			// Update state
			setSuccessMicrodepositType(microdepositType);
			
			
			// Display page message
			dispatch(
				commonActions.showPageMessage({
					color: 'success',
					title: 'Success!',
					message: (
						<React.Fragment>
							Your bank account has been added. The next step is to verify the account using{' '}
							{microdepositType === 'code' ? 'a ' : ''}microdeposit{microdepositType === 'code' ? '' : 's'}.{' '}
							<TextLink
								{...helperFunctions.buttonize(() => {
									modalHelpers.show('microdeposit-info-modal', {
										onClose: () => setSuccessMicrodepositType(undefined),
									});
								})}
							>
								Learn more.
							</TextLink>
						</React.Fragment>
					),
				})
			);
			
			
			// Reset the form and reload payment methods
			resetAfterPaymentMethodAdded();
			
			
			// Return
			return;
		}
		
		
		// Confirm the setup intent
		const setupIntent = await cardSetupIntentHandlerRef.current?.handleSubmit(json.response);
		
		if (!setupIntent) {
			return;
		}
		
		
		// Get the payment method ID
		const paymentMethodID = setupIntent.payment_method;
		
		if (typeof paymentMethodID !== 'string') {
			throw new Error('Unexpected missing payment method on the confirmed setup intent');
		}
		
		
		// Call endpoint
		void dispatch(
			apiActions.call<CheckCardVerificationParameters, undefined, undefined>({
				action: 'CHECK',
				uri: `/website/${window.location.hostname}/member/payment-method/card/verification`,
				parameters: { id: paymentMethodID },
				completion: () => {
					// Reset the form and reload payment methods
					resetAfterPaymentMethodAdded();
				},
			})
		);
	};
	
	
	// Return JSX
	return (
		<React.Fragment>
			<PageTitle>Stored payment methods</PageTitle>
			
			<Note>Stored payment methods are used to conveniently make payments on your community website.</Note>
			
			{paymentMethods?.accountDisabled &&
				(paymentMethods.accountPendingVerification ? (
					<Notice color='yellow'>
						<strong>Heads up.</strong> Payment methods cannot be added until our payment processing partner finishes
						reviewing your community’s information.
					</Notice>
				) : (
					<Notice color='danger'>
						<strong>Oh snap!</strong> Payment methods cannot be added until a website admin provides requested
						information.
					</Notice>
				))}
			
			{paymentMethods && !paymentMethods.accountDisabled && (
				<ButtonGroup>
					<UserControlButton
						text='Add credit/debit card'
						opened={selectedPaymentMethodType === 'card'}
						onClick={() => handleOpen('card')}
						icon={faCreditCard}
					/>
					
					{paymentMethods?.paymentMethodTypes.includes('BANK_ACCOUNT') && (
						<UserControlButton
							text='Add bank account'
							opened={selectedPaymentMethodType === 'us_bank_account'}
							onClick={() => handleOpen('us_bank_account')}
							icon={faUniversity}
						/>
					)}
				</ButtonGroup>
			)}
			
			<FormWrapper>
				<PanelContainer open={Boolean(selectedPaymentMethodType)} onTransitionEnd={handleTransitionEnd}>
					<PanelContainerInner>
						<Panel closeOnClick={handleClose}>
							<APIForm options={formOptions} onCompletion={handleFormCompletion} onCancel={handleClose}>
								<ContentSection open={renderedPaymentMethodType === 'us_bank_account'}>
									<ContentSectionInner>
										<ContentSectionContent>
											{renderedPaymentMethodType === 'us_bank_account' && (
												<BankAccountSetupIntentHandler
													key={formKey}
													ref={bankAccountSetupIntentHandlerRef}
													stripePromise={stripePromise}
												/>
											)}
										</ContentSectionContent>
									</ContentSectionInner>
								</ContentSection>
								
								<ContentSection open={renderedPaymentMethodType === 'card'}>
									<ContentSectionInner>
										<ContentSectionContent>
											{isScreenshot() ? (
												'Stripe payment element placeholder'
											) : (
												<StripeElements
													key={formKey}
													ref={cardSetupIntentHandlerRef}
													currencyCode={paymentMethods?.currencyCode ?? ''}
													stripeAccountID={paymentMethods?.stripeAccountID ?? ''}
													stripePromise={stripePromise}
													selectedPaymentMethodType={renderedPaymentMethodType}
												/>
											)}
										</ContentSectionContent>
									</ContentSectionInner>
								</ContentSection>
							</APIForm>
						</Panel>
					</PanelContainerInner>
				</PanelContainer>
			</FormWrapper>
			
			<Table>
				<thead>
					<tr>
						<th>Method</th>
						<th>Expires</th>
						<th style={{ textAlign: 'center' }}>Status</th>
						<th style={{ textAlign: 'center' }}>Delete</th>
					</tr>
				</thead>
				
				<tbody>
					{paymentMethods?.paymentMethods.map((paymentMethod) => (
						<StoredPaymentMethodRows
							key={paymentMethod.id}
							paymentMethod={paymentMethod}
							onChange={memoizedLoadPaymentMethods}
							onVerify={(unverifiedBankAccount) => setVerificationTarget(unverifiedBankAccount)}
							onLearnMore={(unverifiedBankAccount) => setLearnMoreTarget(unverifiedBankAccount)}
						/>
					))}
					
					{paymentMethods?.paymentMethods?.length === 0 && (
						<EmptyStateRow>You have no stored payment methods yet. Add one using the buttons above.</EmptyStateRow>
					)}
					
					{!paymentMethods && <LoadingRow />}
				</tbody>
			</Table>
			
			<MicrodepositInfoModal microdeposit={learnMoreTarget?.verification.microdeposit ?? successMicrodepositType} />
			
			<VerifyBankAccountModal
				paymentMethod={verificationTarget}
				stripeAccountID={paymentMethods?.stripeAccountID ?? ''}
				onVerificationCompletion={memoizedLoadPaymentMethods}
				stripePromise={stripePromise}
				currencySymbol={paymentMethods?.currencySymbol ?? ''}
			/>
		</React.Fragment>
	);
};

export default Methods;
